import axios, { AxiosInstance } from 'axios'
import {
  TopicDetail,
  TopicsTableData,
  MentionSentiment,
  TopicsOverviewData,
} from 'src/interfaces/topics.interface'
import { HAPTTIC_X_API_KEY } from 'src/constants/env'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'
import {
  AddCompetitorPayload,
  AddReviewPlatformBatchPayload,
  AddReviewPlatformBatchResponse,
  AddReviewPlatformPayload,
  BrandOverviewResponse,
  Competitor,
  CompetitorsResponse,
  CompetitorTarget,
  CreatedLocation,
  CreateReviewAnalysisCampaignPayload,
  FacilityActivityResponse,
  FacilityOverviewResponse,
  GuestReviewsResponse,
  PlatformAnalysis,
  RenameFacilityResponse,
  ReviewAnalysisActiveDateRange,
  ReviewAnalysisCampaign,
  ReviewAnalysisDateRange,
  ReviewAnalysisReviewsResponse,
  ReviewSentiment,
  ReviewsQueryParams,
  ReviewSmartInsightsResponse,
  ReviewTopicDetailResponse,
  ReviewTopicPolarity,
  ReviewTopicsResponse,
  ReviewTopicsScopeParams,
  SpikeBreakdownResponse,
  SubmitLocationsPayload,
  SupportedPlatform,
  UpdateReviewAnalysisCampaignPayload,
  UpdateReviewPlatformPayload,
} from './interfaces/reviewAnalysis.interface'
import {
  ReviewComparisonCreatePayload,
  ReviewComparisonDetail,
  ReviewComparisonPickerFacility,
  ReviewComparisonSummary,
} from './interfaces/reviewComparison.interface'

const BASE = '/v1_private/review-analysis'
const CAMPAIGN_BASE = `${BASE}/campaign`
const campaignPath = (campaignId: string, suffix = '') =>
  `${CAMPAIGN_BASE}/${encodeURIComponent(campaignId)}${suffix}`

// Only emit from/to when both are set, so an inactive filter sends no params.
const dateParams = (range?: ReviewAnalysisDateRange) =>
  range?.from && range?.to ? { from: range.from, to: range.to } : {}

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Api-Key': HAPTTIC_X_API_KEY,
  'x-session-id': getSessionIdFromLocalStorage() || '',
})

// One facility goes as ?facilityId (every backend reads it), several as
// ?facilityIds; none = every facility.
const facilityParams = (ids: string[] = []) =>
  ids.length === 1
    ? { facilityId: ids[0] }
    : ids.length
      ? { facilityIds: ids.join(',') }
      : {}

class ReviewAnalysisApiService {
  private static _instance: ReviewAnalysisApiService

  private client: AxiosInstance

  private constructor() {
    this.client = axios.create({
      baseURL: ANALYTICS_API_URL,
      headers: getHeaders(),
    })
  }

  private static get instance(): ReviewAnalysisApiService {
    if (!ReviewAnalysisApiService._instance) {
      ReviewAnalysisApiService._instance = new ReviewAnalysisApiService()
    }
    return ReviewAnalysisApiService._instance
  }

  public static refresh(): void {
    ReviewAnalysisApiService._instance = new ReviewAnalysisApiService()
  }

  public static async getCampaigns(): Promise<ReviewAnalysisCampaign[]> {
    const response = await ReviewAnalysisApiService.instance.client.get<{
      data: ReviewAnalysisCampaign[]
      success: boolean
    }>(CAMPAIGN_BASE, { headers: getHeaders() })
    return response.data.data
  }

  public static async getCampaign(
    campaignId: string
  ): Promise<ReviewAnalysisCampaign> {
    const response = await ReviewAnalysisApiService.instance.client.get<{
      data: ReviewAnalysisCampaign
      success: boolean
    }>(campaignPath(campaignId), { headers: getHeaders() })
    return response.data.data
  }

  public static async createCampaign(
    payload: CreateReviewAnalysisCampaignPayload
  ): Promise<ReviewAnalysisCampaign> {
    const response = await ReviewAnalysisApiService.instance.client.post<{
      data: ReviewAnalysisCampaign
      success: boolean
    }>(CAMPAIGN_BASE, payload, { headers: getHeaders() })
    return response.data.data
  }

  public static async updateCampaign(
    campaignId: string,
    payload: UpdateReviewAnalysisCampaignPayload
  ): Promise<ReviewAnalysisCampaign> {
    const response = await ReviewAnalysisApiService.instance.client.put<{
      data: ReviewAnalysisCampaign
      success: boolean
    }>(campaignPath(campaignId), payload, { headers: getHeaders() })
    return response.data.data
  }

  public static async deleteCampaign(
    campaignId: string
  ): Promise<{ success: boolean }> {
    const response = await ReviewAnalysisApiService.instance.client.delete<{
      success: boolean
    }>(campaignPath(campaignId), { headers: getHeaders() })
    return response.data
  }

  public static async getStatus(
    campaignId: string
  ): Promise<{ hasPlatforms: boolean }> {
    const response = await ReviewAnalysisApiService.instance.client.get<{
      hasPlatforms: boolean
    }>(campaignPath(campaignId, '/status'), { headers: getHeaders() })
    return response.data
  }

  public static async getOverview(
    campaignId: string,
    range?: ReviewAnalysisDateRange
  ): Promise<BrandOverviewResponse> {
    const response =
      await ReviewAnalysisApiService.instance.client.get<BrandOverviewResponse>(
        campaignPath(campaignId, '/overview'),
        { headers: getHeaders(), params: dateParams(range) }
      )
    return response.data
  }

  public static async getCompetitors(
    campaignId: string
  ): Promise<CompetitorsResponse> {
    const response =
      await ReviewAnalysisApiService.instance.client.get<CompetitorsResponse>(
        campaignPath(campaignId, '/competitors'),
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async addCompetitor(
    campaignId: string,
    payload: AddCompetitorPayload
  ): Promise<{ competitor: Competitor; success: boolean }> {
    const response = await ReviewAnalysisApiService.instance.client.post<{
      competitor: Competitor
      success: boolean
    }>(campaignPath(campaignId, '/competitors'), payload, {
      headers: getHeaders(),
    })
    return response.data
  }

  public static async removeCompetitor(
    campaignId: string,
    competitorId: string
  ): Promise<{ success: boolean }> {
    const response = await ReviewAnalysisApiService.instance.client.delete<{
      success: boolean
    }>(
      campaignPath(
        campaignId,
        `/competitors/${encodeURIComponent(competitorId)}`
      ),
      { headers: getHeaders() }
    )
    return response.data
  }

  public static async searchCompetitorTargets(
    campaignId: string,
    q: string
  ): Promise<{ results: CompetitorTarget[] }> {
    const response = await ReviewAnalysisApiService.instance.client.get<{
      results: CompetitorTarget[]
    }>(campaignPath(campaignId, '/competitor-targets'), {
      headers: getHeaders(),
      params: { q },
    })
    return response.data
  }

  public static async getSupportedPlatforms(campaignId: string): Promise<{
    platforms: SupportedPlatform[]
  }> {
    const response = await ReviewAnalysisApiService.instance.client.get<{
      platforms: SupportedPlatform[]
    }>(campaignPath(campaignId, '/supported-platforms'), {
      headers: getHeaders(),
    })
    return response.data
  }

  public static async getFacilityActivity(
    campaignId: string,
    facilityId: string,
    platformId?: string,
    range?: ReviewAnalysisDateRange,
    venueName?: string
  ): Promise<FacilityActivityResponse> {
    const response =
      await ReviewAnalysisApiService.instance.client.get<FacilityActivityResponse>(
        campaignPath(
          campaignId,
          `/facility/${encodeURIComponent(facilityId)}/activity`
        ),
        {
          headers: getHeaders(),
          params: {
            ...(platformId ? { platformId } : {}),
            ...(venueName ? { venueName } : {}),
            ...dateParams(range),
          },
        }
      )
    return response.data
  }

  // Platform-wide activity (velocity/spike/alerts) across all facilities —
  // the platform page's counterpart of getFacilityActivity.
  public static async getPlatformActivity(
    campaignId: string,
    platformId: string,
    range?: ReviewAnalysisDateRange,
    // the platform page's facility multi-select; [] = every facility
    facilityIds: string[] = []
  ): Promise<FacilityActivityResponse> {
    const response =
      await ReviewAnalysisApiService.instance.client.get<FacilityActivityResponse>(
        campaignPath(
          campaignId,
          `/platform/${encodeURIComponent(platformId)}/activity`
        ),
        {
          headers: getHeaders(),
          params: {
            ...dateParams(range),
            ...facilityParams(facilityIds),
          },
        }
      )
    return response.data
  }

  public static async getFacilityOverview(
    campaignId: string,
    facilityId: string,
    range?: ReviewAnalysisDateRange,
    // Scopes the headline stats to one platform (the page's platform tabs);
    // the per-platform breakdown in the response stays all-platform.
    platformId?: string,
    // Scopes the whole overview to a single location/venue within the facility.
    venueName?: string
  ): Promise<FacilityOverviewResponse> {
    const response =
      await ReviewAnalysisApiService.instance.client.get<FacilityOverviewResponse>(
        campaignPath(
          campaignId,
          `/facility/${encodeURIComponent(facilityId)}/overview`
        ),
        {
          headers: getHeaders(),
          params: {
            ...(platformId ? { platformId } : {}),
            ...(venueName ? { venueName } : {}),
            ...dateParams(range),
          },
        }
      )
    return response.data
  }

  public static async getReviews(
    campaignId: string,
    params: ReviewsQueryParams
  ): Promise<ReviewAnalysisReviewsResponse> {
    const response =
      await ReviewAnalysisApiService.instance.client.get<ReviewAnalysisReviewsResponse>(
        campaignPath(campaignId, '/reviews'),
        {
          headers: getHeaders(),
          params: {
            ...(params.facilityId ? { facilityId: params.facilityId } : {}),
            ...facilityParams(params.facilityIds),
            ...(params.venueName ? { venueName: params.venueName } : {}),
            ...(params.platform ? { platform: params.platform } : {}),
            ...(params.platforms && params.platforms.length
              ? { platforms: params.platforms.join(',') }
              : {}),
            ...(params.sentiment ? { sentiment: params.sentiment } : {}),
            ...(params.ratings && params.ratings.length
              ? { ratings: params.ratings.join(',') }
              : {}),
            ...(params.search ? { search: params.search } : {}),
            // JSON, not CSV: venue names can contain commas.
            ...(params.branches && params.branches.length
              ? {
                  branches: JSON.stringify(
                    params.branches.map((b) => [b.platform, b.venueName])
                  ),
                }
              : {}),
            ...(params.page ? { page: params.page } : {}),
            ...(params.limit ? { limit: params.limit } : {}),
            ...dateParams(params),
          },
        }
      )
    return response.data
  }

  public static async changeReviewSentiment(
    campaignId: string,
    ids: string[],
    sentiment: ReviewSentiment
  ): Promise<{ modified?: number; success?: boolean }> {
    const response = await ReviewAnalysisApiService.instance.client.post<{
      modified?: number
      success?: boolean
    }>(
      campaignPath(campaignId, '/reviews/change-sentiment'),
      { ids, sentiment },
      { headers: getHeaders() }
    )
    return response.data
  }

  public static async getPlatformAnalysis(
    campaignId: string,
    platformId: string,
    // one facility, or the platform page's multi-select ([] = all)
    facilityId?: string | string[],
    range?: ReviewAnalysisDateRange,
    venueName?: string
  ): Promise<PlatformAnalysis> {
    const response =
      await ReviewAnalysisApiService.instance.client.get<PlatformAnalysis>(
        campaignPath(
          campaignId,
          `/platform-analysis/${encodeURIComponent(platformId)}`
        ),
        {
          headers: getHeaders(),
          params: {
            ...(Array.isArray(facilityId)
              ? facilityParams(facilityId)
              : facilityId
                ? { facilityId }
                : {}),
            ...(venueName ? { venueName } : {}),
            ...dateParams(range),
          },
        }
      )
    return response.data
  }

  // Guest Reviews Overview carousel. The read wraps its payload as
  // { success, data }; `facilityIds` is the widget's own multi-select.
  public static async getGuestReviews(
    campaignId: string,
    scope: {
      facilityIds?: string[]
      platform?: string
      range?: ReviewAnalysisDateRange
    } = {}
  ): Promise<GuestReviewsResponse> {
    const response = await ReviewAnalysisApiService.instance.client.get<{
      data: GuestReviewsResponse
    }>(campaignPath(campaignId, '/guest-reviews'), {
      headers: getHeaders(),
      params: {
        ...(scope.platform && scope.platform !== 'all'
          ? { platform: scope.platform }
          : {}),
        ...(scope.facilityIds?.length
          ? { facilityIds: scope.facilityIds.join(',') }
          : {}),
        ...dateParams(scope.range),
      },
    })
    return response.data.data
  }

  public static async getSmartInsights(
    campaignId: string,
    params: {
      facilityId?: string
      from?: string
      platformId?: string
      to?: string
    }
  ): Promise<ReviewSmartInsightsResponse> {
    const response =
      await ReviewAnalysisApiService.instance.client.get<ReviewSmartInsightsResponse>(
        campaignPath(campaignId, '/smart-insights'),
        {
          headers: getHeaders(),
          params: {
            ...(params.facilityId ? { facilityId: params.facilityId } : {}),
            ...(params.platformId ? { platformId: params.platformId } : {}),
            ...(params.from ? { from: params.from } : {}),
            ...(params.to ? { to: params.to } : {}),
          },
        }
      )
    return response.data
  }

  // Topic mention counts for the Top Positive Topics / Top Pain Points widget,
  // plus the tab lists (platforms/venues with tagged reviews in scope).
  public static async getTopics(
    campaignId: string,
    polarity: ReviewTopicPolarity,
    scope: ReviewTopicsScopeParams = {}
  ): Promise<ReviewTopicsResponse> {
    const response =
      await ReviewAnalysisApiService.instance.client.get<ReviewTopicsResponse>(
        campaignPath(campaignId, '/topics'),
        {
          headers: getHeaders(),
          params: {
            polarity,
            ...(scope.facilityId ? { facilityId: scope.facilityId } : {}),
            ...facilityParams(scope.facilityIds),
            ...(scope.platform ? { platform: scope.platform } : {}),
            ...(scope.venueName ? { venueName: scope.venueName } : {}),
            ...dateParams(scope.range),
          },
        }
      )
    return response.data
  }

  // Topics & Subtopics table (the widgets shared with Social Listening). The
  // read wraps its payload as { success, data }.
  // The Topics & Subtopics Overview card (totals, top three, insight band).
  public static async getTopicsAnalysisOverview(
    campaignId: string,
    scope: ReviewTopicsScopeParams = {}
  ): Promise<TopicsOverviewData> {
    const response = await ReviewAnalysisApiService.instance.client.get<{
      data: TopicsOverviewData
    }>(campaignPath(campaignId, '/topics-analysis/overview'), {
      headers: getHeaders(),
      params: {
        ...(scope.facilityId ? { facilityId: scope.facilityId } : {}),
        ...(scope.platform ? { platform: scope.platform } : {}),
        ...(scope.venueName ? { venueName: scope.venueName } : {}),
        ...dateParams(scope.range),
      },
    })
    return response.data.data
  }

  public static async getTopicsAnalysisTable(
    campaignId: string,
    scope: ReviewTopicsScopeParams = {}
  ): Promise<TopicsTableData> {
    const response = await ReviewAnalysisApiService.instance.client.get<{
      data: TopicsTableData
    }>(campaignPath(campaignId, '/topics-analysis'), {
      headers: getHeaders(),
      params: {
        ...(scope.facilityId ? { facilityId: scope.facilityId } : {}),
        ...(scope.platform ? { platform: scope.platform } : {}),
        ...(scope.venueName ? { venueName: scope.venueName } : {}),
        ...dateParams(scope.range),
      },
    })
    return response.data.data
  }

  // One topic, or one subtopic of it, for the modal and its drill-down.
  public static async getTopicsAnalysisDetail(
    campaignId: string,
    name: string,
    subtopic: string | null,
    scope: ReviewTopicsScopeParams = {},
    // The Related Reviews tab: narrows relatedMentions alone.
    sentiment: MentionSentiment | null = null
  ): Promise<TopicDetail> {
    const response = await ReviewAnalysisApiService.instance.client.get<{
      data: TopicDetail
    }>(campaignPath(campaignId, '/topics-analysis/detail'), {
      headers: getHeaders(),
      params: {
        name,
        ...(subtopic ? { subtopic } : {}),
        ...(sentiment ? { sentiment } : {}),
        ...(scope.facilityId ? { facilityId: scope.facilityId } : {}),
        ...(scope.platform ? { platform: scope.platform } : {}),
        ...(scope.venueName ? { venueName: scope.venueName } : {}),
        ...dateParams(scope.range),
      },
    })
    return response.data.data
  }

  // One topic's authored AI summary + the strongest in-scope quotes (derived,
  // so they follow the same tab/date filters as the counts).
  public static async getTopicDetail(
    campaignId: string,
    name: string,
    scope: ReviewTopicsScopeParams = {}
  ): Promise<ReviewTopicDetailResponse> {
    const response =
      await ReviewAnalysisApiService.instance.client.get<ReviewTopicDetailResponse>(
        campaignPath(campaignId, '/topics/detail'),
        {
          headers: getHeaders(),
          params: {
            name,
            ...(scope.facilityId ? { facilityId: scope.facilityId } : {}),
            ...facilityParams(scope.facilityIds),
            ...(scope.platform ? { platform: scope.platform } : {}),
            ...(scope.venueName ? { venueName: scope.venueName } : {}),
            ...dateParams(scope.range),
          },
        }
      )
    return response.data
  }

  // Days (YYYY-MM-DD) in `month` (YYYY-MM) that have at least one review —
  // drives the data dots in the custom-range calendar.
  // facilityId narrows the dots to one facility of the campaign.
  public static async getCalendarDates(
    campaignId: string,
    month: string,
    // Optional narrowing to one facility / platform of the campaign.
    scope: { facilityId?: string; platformId?: string } = {}
  ): Promise<string[]> {
    const params: Record<string, string> = { month }
    if (scope.facilityId) params.facilityId = scope.facilityId
    if (scope.platformId) params.platformId = scope.platformId
    const response = await ReviewAnalysisApiService.instance.client.get<{
      dates?: string[]
    }>(campaignPath(campaignId, '/calendar-dates'), {
      headers: getHeaders(),
      params,
    })
    return response.data.dates ?? []
  }

  // The campaign's own first/last review date. The top-bar picker uses it for
  // the "Lifetime" preset on review-analysis routes — the account-wide range it
  // otherwise uses spans social-listening mentions, which can start long after
  // the reviews do.
  public static async getActiveDateRange(
    campaignId: string
  ): Promise<ReviewAnalysisActiveDateRange> {
    const response =
      await ReviewAnalysisApiService.instance.client.get<ReviewAnalysisActiveDateRange>(
        campaignPath(campaignId, '/active-date-range'),
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async getSpikeBreakdown(
    campaignId: string,
    date?: string,
    range?: ReviewAnalysisDateRange
  ): Promise<SpikeBreakdownResponse> {
    const response =
      await ReviewAnalysisApiService.instance.client.get<SpikeBreakdownResponse>(
        campaignPath(campaignId, '/spike-breakdown'),
        {
          headers: getHeaders(),
          params: {
            ...(date ? { date } : {}),
            ...dateParams(range),
          },
        }
      )
    return response.data
  }

  public static async addReviewPlatform(
    campaignId: string,
    payload: AddReviewPlatformPayload
  ): Promise<{ platform: unknown; success: boolean }> {
    const response = await ReviewAnalysisApiService.instance.client.post<{
      platform: unknown
      success: boolean
    }>(campaignPath(campaignId, '/platforms'), payload, {
      headers: getHeaders(),
    })
    return response.data
  }

  public static async addReviewPlatformsBatch(
    campaignId: string,
    payload: AddReviewPlatformBatchPayload
  ): Promise<AddReviewPlatformBatchResponse> {
    const response =
      await ReviewAnalysisApiService.instance.client.post<AddReviewPlatformBatchResponse>(
        campaignPath(campaignId, '/platforms/batch'),
        payload,
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async updateReviewPlatform(
    campaignId: string,
    cardId: string,
    payload: UpdateReviewPlatformPayload
  ): Promise<{ platform: unknown; success: boolean }> {
    const response = await ReviewAnalysisApiService.instance.client.patch<{
      platform: unknown
      success: boolean
    }>(
      campaignPath(campaignId, `/platforms/${encodeURIComponent(cardId)}`),
      payload,
      { headers: getHeaders() }
    )
    return response.data
  }

  // Name and/or category (null clears it); send only what changed.
  public static async renameFacility(
    campaignId: string,
    facilityId: string,
    changes: { category?: string | null; name?: string }
  ): Promise<RenameFacilityResponse> {
    const response =
      await ReviewAnalysisApiService.instance.client.patch<RenameFacilityResponse>(
        campaignPath(campaignId, `/facility/${encodeURIComponent(facilityId)}`),
        changes,
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async deleteFacility(
    campaignId: string,
    facilityId: string
  ): Promise<{ success: boolean }> {
    const response = await ReviewAnalysisApiService.instance.client.delete<{
      success: boolean
    }>(
      campaignPath(campaignId, `/facility/${encodeURIComponent(facilityId)}`),
      { headers: getHeaders() }
    )
    return response.data
  }

  public static async deleteReviewPlatform(
    campaignId: string,
    cardId: string
  ): Promise<{ success: boolean }> {
    const response = await ReviewAnalysisApiService.instance.client.delete<{
      success: boolean
    }>(campaignPath(campaignId, `/platforms/${encodeURIComponent(cardId)}`), {
      headers: getHeaders(),
    })
    return response.data
  }

  /* ── Reporting & Comparison ─────────────────────────────────────────── */

  // What the picker can offer: the facilities this campaign has reviews for.
  // A facility with none is nothing to compare.
  public static async getComparisonFacilities(
    campaignId: string
  ): Promise<ReviewComparisonPickerFacility[]> {
    const response = await ReviewAnalysisApiService.instance.client.get<{
      data: ReviewComparisonPickerFacility[]
      success: boolean
    }>(campaignPath(campaignId, '/comparison-facilities'), {
      headers: getHeaders(),
    })
    return response.data.data ?? []
  }

  public static async getComparisons(
    campaignId: string
  ): Promise<ReviewComparisonSummary[]> {
    const response = await ReviewAnalysisApiService.instance.client.get<{
      data: ReviewComparisonSummary[]
      success: boolean
    }>(campaignPath(campaignId, '/comparisons'), { headers: getHeaders() })
    return response.data.data ?? []
  }

  // The report, computed by the backend on this read — which is why there is
  // nothing to poll and no status to wait on.
  public static async getComparison(
    campaignId: string,
    comparisonId: string
  ): Promise<ReviewComparisonDetail> {
    const response = await ReviewAnalysisApiService.instance.client.get<{
      data: ReviewComparisonDetail
      success: boolean
    }>(campaignPath(campaignId, `/comparisons/${comparisonId}`), {
      headers: getHeaders(),
    })
    return response.data.data
  }

  public static async createComparison(
    campaignId: string,
    payload: ReviewComparisonCreatePayload
  ): Promise<ReviewComparisonSummary> {
    const response = await ReviewAnalysisApiService.instance.client.post<{
      data: ReviewComparisonSummary
      success: boolean
    }>(campaignPath(campaignId, '/comparisons'), payload, {
      headers: getHeaders(),
    })
    return response.data.data
  }

  public static async deleteComparison(
    campaignId: string,
    comparisonId: string
  ): Promise<void> {
    await ReviewAnalysisApiService.instance.client.delete(
      campaignPath(campaignId, `/comparisons/${comparisonId}`),
      { headers: getHeaders() }
    )
  }

  // Fetched as a blob and saved through an object URL rather than navigating
  // to the endpoint: it needs the session headers, which a plain window.open
  // cannot send. The deck is rendered on demand, so this takes as long as a
  // render — the caller shows its downloading state until it resolves.
  public static async downloadComparison(
    campaignId: string,
    comparisonId: string,
    fallbackFilename: string
  ): Promise<void> {
    const response = await ReviewAnalysisApiService.instance.client.get(
      campaignPath(campaignId, `/comparisons/${comparisonId}/download`),
      { headers: getHeaders(), responseType: 'blob' }
    )

    const header = response.headers['content-disposition']
    const named =
      typeof header === 'string'
        ? header.match(/filename="?([^";]+)"?/)?.[1]
        : undefined

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    // The server names the file after the facilities and the window, so that
    // name wins over anything reconstructed here.
    link.download = named || fallbackFilename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  public static async submitLocations(
    campaignId: string,
    payload: SubmitLocationsPayload
  ): Promise<{ locations: CreatedLocation[]; success: boolean }> {
    const response = await ReviewAnalysisApiService.instance.client.post<{
      locations: CreatedLocation[]
      success: boolean
    }>(campaignPath(campaignId, '/locations'), payload, {
      headers: getHeaders(),
    })
    return response.data
  }
}

export { ReviewAnalysisApiService }
